import {
  KButton,
  KDatePicker,
  KDialog,
  KSelectMulti,
  KTextInput,
} from "@kyburz/kdesign-system";
import { makeStyles } from "@material-ui/core";
import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import useSetState from "../../../hooks/useTypedSetState";
import { setAlert } from "../../../redux/actions/actions";
import { generateBillingInvoice } from "../../../apiCalls/admin/billing_invoices";
import { getCustomers } from "../../../apiCalls/auth";
import KSwitch from "../../../components/ui/button/switch";

const useStyles = makeStyles(() => ({
  container: {
    display: "flex",
    flexDirection: "column",
  },
  parametersContainer: {
    display: "flex",
    flexDirection: "row",
    marginTop: 16,
  },
  datePicker: {
    marginRight: 20,
  },
  filtersContainer: {
    borderTop: "1px solid #e0e0e0",
    marginTop: 16,
    paddingTop: 16,
  },
  filtersInput: {
    marginBottom: 8,
  },
}));

interface Props {
  open: boolean;
  handleClose: (withReload: boolean) => void;
}

const NOW = new Date();

const NewInternalInvoice = ({ open, handleClose }: Props) => {
  const dispatch = useDispatch();
  const classes = useStyles();

  const INIT_STATE = {
    fromDate: new Date(
      Date.UTC(NOW.getUTCFullYear(), NOW.getUTCMonth() + 1, 1),
    ),
    includeVehicles: true,
    filters: "",
    companies: [],
  };

  const [
    { fromDate, companies, includeVehicles, loading, allCompanies, filters },
    setState,
  ] = useSetState({
    ...INIT_STATE,
    allCompanies: [],
    loading: false,
  });

  useEffect(() => {
    getCustomers().then((res) => {
      const childNames = res.data.reduce((acc, x) => {
        if (x.company_internals?.parent_company_id) {
          acc[x.company_internals.parent_company_id] =
            acc[x.company_internals.parent_company_id] || [];
          acc[x.company_internals.parent_company_id].push(x.name);
        }
        return acc;
      }, {});
      setState({
        allCompanies: res.data
          .filter((x) => !x.company_internals?.parent_company_id)
          .map((x) => ({
            name: childNames[x.shortname]
              ? `${x.name} (${childNames[x.shortname].join(", ")})`
              : x.name,
            id: x.shortname,
          })),
      });
    });
  }, []);

  const isSubmitDisabled = !fromDate || !companies.length || loading;

  const closePopup = (reload: any) => {
    handleClose(reload);
    setState({ ...INIT_STATE });
  };

  const onSubmit = () => {
    setState({ loading: true });
    generateBillingInvoice(companies, fromDate, includeVehicles, filters)
      .then(() => {
        dispatch(setAlert("Invoice being generated", "info"));
      })
      .finally(() => {
        setState({ loading: false });
        handleClose(true);
      });
  };

  const onDateChange = (date) => setState({ fromDate: date });

  const handleMultiSelectChange = (_, value): void => {
    setState({
      companies: value.map((x) => x.id),
    });
  };

  const handleToggleChange = (e) =>
    setState({
      [e.target.name]: e.target.checked,
    });

  const handleFiltersChange = (e) =>
    setState({
      [e.target.name]: e.target.value,
    });

  const setOnlyFleetboxFilter = () =>
    setState({
      filters: "has_fleet_box:eq:true",
    });

  return (
    <KDialog
      open={open}
      title="New Invoice"
      size="sm"
      onClose={() => closePopup(false)}
      primaryCTAonClick={onSubmit}
      primaryCTAText="Create"
      primaryCTAColor="primary"
      secondaryCTAonClick={() => closePopup(false)}
      secondaryCTAText="Cancel"
      hasOverflow
      disabled={isSubmitDisabled}
      contentClassName={classes.container}
    >
      <KSelectMulti
        onChange={handleMultiSelectChange}
        label="Companies"
        elements={allCompanies}
        name="companies"
      />
      <div className={classes.parametersContainer}>
        <KDatePicker
          label="From Date"
          selected={fromDate}
          onChange={onDateChange}
          rootClassName={classes.datePicker}
        />
        <KSwitch
          name="includeVehicles"
          onChange={handleToggleChange}
          checked={includeVehicles}
          label="Include Detailed Vehicles"
        />
      </div>
      <div className={classes.filtersContainer}>
        <KTextInput
          onChange={handleFiltersChange}
          value={filters}
          label="Vehicle Filters"
          placeholder="name:eq:Batmobile"
          name="filters"
          rootClassName={classes.filtersInput}
        />
        <KButton
          size="small"
          type="button"
          theme="tertiary"
          onClick={setOnlyFleetboxFilter}
        >
          Only with fleetboxes
        </KButton>
      </div>
    </KDialog>
  );
};

export default NewInternalInvoice;
